import React, { useMemo } from 'react';

/**
 * convert ref as a function
 * @param {React.RefObject} ref
 * @returns
 */
const toFnRef = (ref) =>
  !ref || typeof ref === 'function'
    ? ref
    : (value) => {
        ref.current = value;
      };

/**
 * Create and returns a single callback ref composed from two other Refs.
 *
 * ```tsx
 * const Button = React.forwardRef((props, ref) => {
 *   const [element, attachRef] = useCallbackRef<HTMLButtonElement>();
 *   const mergedRef = useMergedRefs(ref, attachRef);
 *
 *   return <button ref={mergedRef} {...props}/>
 * })
 * ```
 * @param {React.RefObject} refA A Callback or mutable Ref
 * @param {React.RefObject} refB A Callback or mutable Ref
 * @category refs
 */
const useMergedRefs = (refA, refB) => {
  const mergeRefs = (refA, refB) => {
    const a = toFnRef(refA);
    const b = toFnRef(refB);
    return (value) => {
      if (a) a(value);
      if (b) b(value);
    };
  };

  return useMemo(() => mergeRefs(refA, refB), [refA, refB]);
}

export default useMergedRefs;
